import { Outlet, useParams } from "react-router";
import { I18nextProvider, useTranslation } from "react-i18next";
import { liveLocale } from "./locales";
import { LocaleContext } from "./LocaleContext";
import { instanceFor } from "./instances";
import { CmsPreviewBridge } from "./CmsPreviewBridge";
import NotFound from "../screens/NotFound";

/**
 * AC6 cross-locale isolation canary. Reads `i18n.language` from the i18next
 * instance bound by the ENCLOSING `<I18nextProvider>` (the context, NOT
 * `useParams`), then emits it as a zero-visual `data-locale` attribute in the
 * prerendered HTML. The smoke-check (`scripts/seo-audit.mjs`) asserts this value
 * equals the page's path segment for every sampled `/en/*` and `/nl/*` page —
 * proving each page rendered against its own instance with no string bleed
 * across the single-process prerender (NFR6 / G3).
 */
function LocaleCanary() {
  const { i18n } = useTranslation();
  return <div data-locale={i18n.language} hidden aria-hidden="true" />;
}

/**
 * LocaleLayout — the `:lang` segment wrapper (Story 4.1, AC2/AC3).
 *
 * Reads `params.lang`, resolves it against the live locales, and:
 *  - unknown prefix (e.g. /zz/features) → renders the 404 / NotFound screen.
 *    (Legacy *unprefixed* paths never reach here in production — server.js 301s
 *    them to the x-default `/nl/…` before the client boots; see server.js.)
 *  - valid locale → provides the `LocaleDef` to descendants via LocaleContext,
 *    binds react-i18next to that locale's fixed instance via `<I18nextProvider>`
 *    (Story 4.2, AC2), and renders the matched route's `<Outlet />`.
 */
export default function LocaleLayout() {
  const def = liveLocale(useParams().lang);
  if (!def) return <NotFound />;
  return (
    <LocaleContext.Provider value={def}>
      <I18nextProvider i18n={instanceFor(def.code)}>
        <LocaleCanary />
        <CmsPreviewBridge />
        <Outlet />
      </I18nextProvider>
    </LocaleContext.Provider>
  );
}
